import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  Input,
  Label,
  PageTitle,
} from '@dataroom/shared';
import {
  formatWhen,
  PROOFS,
  REFUSAL_LABELS,
  STATUS_LABELS,
  type FirmDetail,
  type FirmMatch,
  type RecoveryCaseStatus,
  type RecoveryProof,
  type StaffRecoveryCase,
} from '@/lib/ownership-recovery';

/**
 * Platform staff's "Assign owner" (ADR-0055 §4): when every owner of a firm has
 * lost access, find the firm, check out of band who is asking (the page shows
 * the owners' last activity, the recovery contact, the Stripe payer, and where
 * the person stands in the firm), record at least two proofs with a note and a
 * reason, and confirm with a fresh authenticator code. With more than one staff
 * member, a second, different one approves — and a reversal is asked by one and
 * carried out by another. Then 72 hours (from delivered notices) in which any
 * owner can cancel; a completed recovery can be reversed for 30 days. The server
 * checks everything; a 404 from the API (not platform staff) sends the person home.
 */

const CASES_KEY = ['platform', 'ownership-recovery'];
const FILTERS: { value: RecoveryCaseStatus | 'all'; label: string }[] = [
  { value: 'awaiting_second_approval', label: 'Needs a second approval' },
  { value: 'approved', label: 'Waiting 72 hours' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All' },
];
const textareaClass = 'min-h-16 w-full rounded-md border bg-background px-3 py-2 text-sm';

interface CasesResponse {
  cases: StaffRecoveryCase[];
  staffCount: number;
  me: string;
}

export function PlatformRecoveryPage() {
  const [filter, setFilter] = useState<RecoveryCaseStatus | 'all'>('all');
  const cases = useQuery({
    queryKey: [...CASES_KEY, filter],
    queryFn: () =>
      api.get<CasesResponse>(
        `/platform/ownership-recovery/cases${filter === 'all' ? '' : `?status=${filter}`}`,
      ),
    retry: false,
  });

  if (cases.error instanceof ApiError && cases.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  if (cases.error instanceof ApiError && cases.error.status === 404) {
    return <Navigate to="/" replace />;
  }
  const staffCount = cases.data?.staffCount ?? 1;

  return (
    <div className="mx-auto w-full max-w-container space-y-6 px-4 py-6 md:px-6">
      <PageTitle>Ownership recovery</PageTitle>
      <AssignOwner staffCount={staffCount} />
      <section className="space-y-3" aria-label="Recoveries">
        <h2 className="text-lg font-semibold">Recoveries</h2>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <Button
              key={f.value}
              size="sm"
              variant={filter === f.value ? 'default' : 'outline'}
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        {cases.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : cases.error ? (
          <ErrorText error={cases.error} />
        ) : cases.data.cases.length === 0 ? (
          <p className="text-sm text-muted-foreground">None.</p>
        ) : (
          <ul className="space-y-4" aria-label="Recovery cases">
            {cases.data.cases.map((c) => (
              <li key={c.id}>
                <CaseCard c={c} me={cases.data.me} staffCount={staffCount} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const PAYER_TEXT = (firm: FirmDetail) =>
  firm.payer.lookup === 'ok'
    ? (firm.payer.email ?? '')
    : firm.payer.lookup === 'failed'
      ? 'could not be read from Stripe — try again before relying on it'
      : 'none on file';

function FirmFacts({ firm }: { firm: FirmDetail }) {
  const s = firm.assignee;
  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[12rem_1fr]">
      <dt className="text-muted-foreground">Owners</dt>
      <dd>
        <ul aria-label="Owners">
          {firm.owners.map((o) => (
            <li key={o.actorId}>
              {o.email} — last sign-in {o.lastSignInAt ? formatWhen(o.lastSignInAt) : 'never'}
              {o.lastSeenAt ? `, last active ${formatWhen(o.lastSeenAt)}` : ''} ·{' '}
              {o.mfaEnrolled ? 'two-step sign-in set up' : 'no two-step sign-in'}
            </li>
          ))}
        </ul>
      </dd>
      <dt className="text-muted-foreground">Recovery contact</dt>
      <dd>{firm.recoveryContact ?? 'none'}</dd>
      <dt className="text-muted-foreground">Stripe payer</dt>
      <dd>{PAYER_TEXT(firm)}</dd>
      {firm.inFlight ? (
        <>
          <dt className="text-muted-foreground">In progress</dt>
          <dd>{STATUS_LABELS[firm.inFlight.status]}</dd>
        </>
      ) : null}
      {s ? (
        <>
          <dt className="text-muted-foreground">{s.email}</dt>
          <dd aria-label="Standing">
            {s.exists
              ? `has an account here (${s.tenantRole ?? 'no firm role'}; email ${
                  s.emailVerified ? 'confirmed' : 'not confirmed'
                }; ${s.mfaEnrolled ? 'two-step sign-in set up' : 'no two-step sign-in'}${
                  s.buyerTeam ? '; on a buyer team' : ''
                }${s.boundSeller ? '; a bound seller' : ''}${
                  s.roomGrants > 0 ? `; ${s.roomGrants} buyer/seller room grant(s)` : ''
                }${s.dealRecords ? '; in a buyer funnel or signing a room document' : ''})`
              : 'no account here yet — they will be invited'}
            {s.refusal ? (
              <span className="block text-destructive">
                Cannot be assigned: {REFUSAL_LABELS[s.refusal] ?? s.refusal}
              </span>
            ) : null}
          </dd>
        </>
      ) : null}
    </dl>
  );
}

function AssignOwner({ staffCount }: { staffCount: number }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [search, setSearch] = useState<string | null>(null);
  const [firmId, setFirmId] = useState<string | null>(null);
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [checked, setChecked] = useState<string | null>(null);
  const [proofs, setProofs] = useState<RecoveryProof[]>([]);
  const [proofNote, setProofNote] = useState('');
  const [reason, setReason] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [done, setDone] = useState<StaffRecoveryCase | null>(null);

  const firms = useQuery({
    queryKey: [...CASES_KEY, 'firms', search],
    queryFn: () =>
      api
        .get<{
          firms: FirmMatch[];
        }>(`/platform/ownership-recovery/firms?q=${encodeURIComponent(search ?? '')}`)
        .then((r) => r.firms),
    enabled: search !== null,
    retry: false,
  });
  const detail = useQuery({
    queryKey: [...CASES_KEY, 'firm', firmId, checked],
    queryFn: () =>
      api.get<FirmDetail>(
        `/platform/ownership-recovery/firms/${firmId}${
          checked ? `?assignee=${encodeURIComponent(checked)}` : ''
        }`,
      ),
    enabled: firmId !== null,
    // Checking a person refetches the firm: keep the form on screen meanwhile.
    placeholderData: (previous) => (previous?.tenantId === firmId ? previous : undefined),
    retry: false,
  });

  const assign = useMutation({
    mutationFn: () =>
      api.post<StaffRecoveryCase>('/platform/ownership-recovery/cases', {
        tenantId: firmId,
        assigneeEmail: assigneeEmail.trim(),
        proofs,
        proofNote: proofNote.trim(),
        reason: reason.trim(),
        totpCode: totpCode.trim(),
      }),
    onSuccess: (c) => {
      setDone(c);
      setFirmId(null);
      setChecked(null);
      setAssigneeEmail('');
      setProofs([]);
      setProofNote('');
      setReason('');
      setTotpCode('');
      void qc.invalidateQueries({ queryKey: CASES_KEY });
    },
  });

  function find(e: FormEvent) {
    e.preventDefault();
    setFirmId(null);
    setDone(null);
    setSearch(q.trim());
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    assign.mutate();
  }
  const toggle = (p: RecoveryProof) =>
    setProofs((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  const firm = detail.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assign an owner</CardTitle>
        <p className="text-sm text-muted-foreground">
          For a firm whose owners have all lost access. Verify who is asking yourself — by phone,
          from Stripe, from the recovery contact — before you record it here.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={find}>
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="firm-q">Firm name, firm ID, or a member’s email</Label>
            <Input id="firm-q" required value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Button type="submit" variant="outline">
            Find firm
          </Button>
        </form>
        <ErrorText error={firms.error} />
        {firms.data && !firmId ? (
          firms.data.length === 0 ? (
            <p className="text-muted-foreground">No firm found.</p>
          ) : (
            <ul className="space-y-2" aria-label="Firms found">
              {firms.data.map((f) => (
                <li key={f.tenantId} className="flex items-center justify-between gap-3">
                  <span>{f.name ?? f.tenantId}</span>
                  <Button size="sm" onClick={() => setFirmId(f.tenantId)}>
                    Choose
                  </Button>
                </li>
              ))}
            </ul>
          )
        ) : null}

        {firmId && detail.isPending ? <p className="text-muted-foreground">Loading…</p> : null}
        <ErrorText error={detail.error} />
        {firmId && firm ? (
          <form className="space-y-3" onSubmit={submit} aria-label="Assign owner">
            <div className="font-medium">{firm.name}</div>
            <FirmFacts firm={firm} />
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="assignee">Email of the person to make an owner</Label>
                <Input
                  id="assignee"
                  type="email"
                  required
                  value={assigneeEmail}
                  onChange={(e) => setAssigneeEmail(e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={!assigneeEmail.trim()}
                onClick={() => setChecked(assigneeEmail.trim())}
              >
                Check this person
              </Button>
            </div>
            <fieldset className="space-y-1">
              <legend className="font-medium">What you verified (at least two)</legend>
              {PROOFS.map((p) => (
                <label key={p.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="proof"
                    value={p.value}
                    checked={proofs.includes(p.value)}
                    onChange={() => toggle(p.value)}
                  />
                  {p.label}
                </label>
              ))}
            </fieldset>
            <div className="space-y-1.5">
              <Label htmlFor="proof-note">How you verified them</Label>
              <textarea
                id="proof-note"
                className={textareaClass}
                required
                minLength={10}
                maxLength={2000}
                value={proofNote}
                onChange={(e) => setProofNote(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-reason">Reason</Label>
              <textarea
                id="assign-reason"
                className={textareaClass}
                required
                minLength={10}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="assign-code">Your authenticator code</Label>
                <Input
                  id="assign-code"
                  inputMode="numeric"
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={assign.isPending || proofs.length < 2}>
                {staffCount > 1 ? 'Ask for a second approval' : 'Assign owner — start 72 hours'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setFirmId(null)}>
                Back
              </Button>
            </div>
            <ErrorText error={assign.error} />
          </form>
        ) : null}
        {done ? (
          <p role="status">
            {done.status === 'awaiting_second_approval'
              ? `Recorded. Another staff member must approve making ${done.assigneeEmail} an owner of ${done.firmName ?? 'the firm'} within 7 days.`
              : `Approved. ${done.assigneeEmail} becomes an owner of ${done.firmName ?? 'the firm'} no sooner than 72 hours after the notices go out, unless an owner cancels.`}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function noticeState(c: StaffRecoveryCase): string | null {
  if (c.status !== 'approved') return null;
  if (c.noticesSentAt) return `Notices delivered ${formatWhen(c.noticesSentAt)}`;
  if (c.noticesFailedAt) {
    return `Notices not delivered (${c.noticeAttempts} attempts) — it will not take effect. Stop it and assign again once mail works.`;
  }
  return c.noticeAttempts > 0
    ? `Notices not delivered yet (${c.noticeAttempts} failed attempt(s), retrying) — the 72 hours have not started`
    : 'Notices are being sent — the 72 hours start once they are delivered';
}

function CaseCard({ c, me, staffCount }: { c: StaffRecoveryCase; me: string; staffCount: number }) {
  const qc = useQueryClient();
  const [totpCode, setTotpCode] = useState('');
  const [closeCode, setCloseCode] = useState('');
  const [reason, setReason] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: CASES_KEY });
  const act = useMutation({
    mutationFn: (kind: 'approve' | 'cancel' | 'reverse') =>
      api.post(
        `/platform/ownership-recovery/cases/${c.id}/${kind}`,
        kind === 'approve'
          ? { totpCode: totpCode.trim() }
          : { totpCode: closeCode.trim(), reason: reason.trim() },
      ),
    onSuccess: () => {
      setTotpCode('');
      setCloseCode('');
      setReason('');
      refresh();
    },
  });

  const inFlight = c.status === 'awaiting_second_approval' || c.status === 'approved';
  const canApprove = c.status === 'awaiting_second_approval' && c.requestedByEmail !== me;
  const reversible =
    c.status === 'completed' &&
    c.reversibleUntil !== null &&
    new Date(c.reversibleUntil).getTime() > Date.now();
  const twoPerson = c.twoPerson || staffCount > 1;
  const myReversalRequest =
    c.reversalRequestedByEmail !== null && c.reversalRequestedByEmail === me;
  const reverseLabel = !twoPerson
    ? 'Reverse this recovery'
    : c.reversalRequestedByEmail
      ? 'Confirm the reversal'
      : 'Ask to reverse (a second staff member confirms)';
  const proofLabel = (p: RecoveryProof) => PROOFS.find((x) => x.value === p)?.label ?? p;
  const notices = noticeState(c);

  function submit(e: FormEvent, kind: 'approve' | 'cancel' | 'reverse') {
    e.preventDefault();
    act.mutate(kind);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{c.firmName ?? c.tenantId}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {STATUS_LABELS[c.status]} · asked {formatWhen(c.requestedAt)} by {c.requestedByEmail}
          {c.approvedByEmail && c.approvedByEmail !== c.requestedByEmail
            ? ` · approved by ${c.approvedByEmail}`
            : ''}
          {c.effectiveAt && c.status === 'approved' && c.noticesSentAt
            ? ` · takes effect ${formatWhen(c.effectiveAt)}`
            : ''}
        </p>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[12rem_1fr]">
          <dt className="text-muted-foreground">New owner</dt>
          <dd>{c.assigneeEmail}</dd>
          <dt className="text-muted-foreground">Verified</dt>
          <dd>{c.proofs.map(proofLabel).join(' · ')}</dd>
          <dt className="text-muted-foreground">How</dt>
          <dd className="whitespace-pre-wrap">{c.proofNote}</dd>
          <dt className="text-muted-foreground">Reason</dt>
          <dd className="whitespace-pre-wrap">{c.reason}</dd>
          {notices ? (
            <>
              <dt className="text-muted-foreground">Notices</dt>
              <dd className={c.noticesFailedAt ? 'text-destructive' : undefined}>{notices}</dd>
            </>
          ) : null}
          {c.reversalRequestedByEmail && c.status === 'completed' ? (
            <>
              <dt className="text-muted-foreground">Reversal asked</dt>
              <dd>
                by {c.reversalRequestedByEmail}
                {c.reversalReason ? ` — ${c.reversalReason}` : ''}
              </dd>
            </>
          ) : null}
          {c.closeReason ? (
            <>
              <dt className="text-muted-foreground">Closed</dt>
              <dd>
                {c.closeReason.replace(/_/g, ' ')}
                {c.closedByEmail ? ` (${c.closedByEmail})` : ''}
              </dd>
            </>
          ) : null}
        </dl>

        {c.status === 'awaiting_second_approval' && !canApprove ? (
          <p className="text-muted-foreground">
            You asked for this — another staff member must approve it.
          </p>
        ) : null}
        {reversible && myReversalRequest ? (
          <p className="text-muted-foreground">
            You asked to reverse this — another staff member must confirm it.
          </p>
        ) : null}

        {canApprove ? (
          <form
            className="flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(e) => submit(e, 'approve')}
            aria-label="Approve"
          >
            <div className="space-y-1.5">
              <Label htmlFor={`code-${c.id}`}>Your authenticator code</Label>
              <Input
                id={`code-${c.id}`}
                inputMode="numeric"
                required
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={act.isPending}>
              Approve — start 72 hours
            </Button>
          </form>
        ) : null}

        {inFlight || (reversible && !myReversalRequest) ? (
          <form
            className="space-y-2"
            onSubmit={(e) => submit(e, reversible ? 'reverse' : 'cancel')}
            aria-label={reversible ? 'Reverse' : 'Stop'}
          >
            <div className="space-y-1.5">
              <Label htmlFor={`reason-${c.id}`}>
                {reversible ? 'Why reverse this recovery' : 'Why stop this recovery'}
              </Label>
              <textarea
                id={`reason-${c.id}`}
                className={textareaClass}
                required
                minLength={10}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor={`rcode-${c.id}`}>Your authenticator code</Label>
                <Input
                  id={`rcode-${c.id}`}
                  inputMode="numeric"
                  required
                  value={closeCode}
                  onChange={(e) => setCloseCode(e.target.value)}
                />
              </div>
              <Button type="submit" variant="outline" disabled={act.isPending}>
                {reversible ? reverseLabel : 'Stop this recovery'}
              </Button>
            </div>
          </form>
        ) : null}
        <ErrorText error={act.error} />
      </CardContent>
    </Card>
  );
}
